import { GameMatchStatus, OpticOddsFixtureStatus, PeriodType } from '../enums';
import { GameContext, RetirementInfo } from '../interfaces';

export const readRetirementInfo = (result: any): RetirementInfo | null => {
	const raw = result?.score?.retirement_info ?? result?.retirement_info ?? null;
	if (!raw || typeof raw !== 'object') return null;
	if (raw.walkover == null && !raw.winner && !raw.winner_team_id) return null;

	return {
		winner: String(raw.winner ?? ''),
		winnerTeamId: String(raw.winner_team_id ?? ''),
		walkover: Boolean(raw.walkover),
	};
};

export const readFixtureStatus = (result: any): string | null => {
	const status = result?.score?.fixture?.status ?? result?.status ?? null;
	const sportId = result?.score?.sport?.id;
	const inPlay = result?.score?.in_play;

	if (sportId === 'soccer' && inPlay?.period === '1H' && inPlay.clock === '45') {
		return OpticOddsFixtureStatus.Half;
	}

	if (sportId === 'football' && inPlay?.period === '2' && inPlay.clock === '00:00') {
		return OpticOddsFixtureStatus.Half;
	}

	return status;
};

export const isOpticOddsSuspended = (status: string | null | undefined): boolean =>
	status === OpticOddsFixtureStatus.Suspended;

export const isOpticOddsCompleted = (status: string | null | undefined): boolean =>
	status === OpticOddsFixtureStatus.Completed;

export const isOpticOddsLive = (status: string | null | undefined): boolean =>
	status === OpticOddsFixtureStatus.Live;

/** OO explicitly says the fixture has not started: scheduled, or late off the blocks. */
export const isOpticOddsAwaitingStart = (status: string | null | undefined): boolean =>
	status === OpticOddsFixtureStatus.Unplayed || status === OpticOddsFixtureStatus.Delayed;

/** The identity of both sides, as far as an OO fixture reports it. */
export type FixtureSides = {
	homeTeam?: string | null;
	awayTeam?: string | null;
	homeCompetitorIds?: string[] | null;
	awayCompetitorIds?: string[] | null;
};

const normalizeName = (name: string): string => name.trim().toLowerCase();

/**
 * Which side retirement_info names as the winner, or null when it cannot be resolved.
 *
 * winner_team_id is matched first because it is a stable identifier, while the display
 * name is a formatting choice: OO may report "Carlos Alcaraz" in retirement_info while
 * home_team_display reads "C. Alcaraz", and a name-only match would silently fail.
 * Callers must treat null as "unknown" and never guess from the score — on a retirement
 * the player who quit can be the one ahead.
 */
export const retirementWinnerSide = (
	info: RetirementInfo | null | undefined,
	sides: FixtureSides
): 'home' | 'away' | null => {
	if (!info) return null;

	if (info.winnerTeamId) {
		if (sides.homeCompetitorIds?.includes(info.winnerTeamId)) return 'home';
		if (sides.awayCompetitorIds?.includes(info.winnerTeamId)) return 'away';
	}

	if (info.winner) {
		const winner = normalizeName(info.winner);
		if (sides.homeTeam && normalizeName(sides.homeTeam) === winner) return 'home';
		if (sides.awayTeam && normalizeName(sides.awayTeam) === winner) return 'away';
	}

	return null;
};

export const isTerminalGameMatchStatus = (status: GameMatchStatus): boolean =>
	status === GameMatchStatus.Final ||
	status === GameMatchStatus.Walkover ||
	status === GameMatchStatus.Retired ||
	status === GameMatchStatus.Abandoned ||
	status === GameMatchStatus.Cancelled;

/** The statuses that follow from the fixture alone, with no in-play data involved. */
export type FixtureMatchStatus =
	| GameMatchStatus.Walkover
	| GameMatchStatus.Retired
	| GameMatchStatus.Final
	| GameMatchStatus.Suspended
	| GameMatchStatus.Abandoned
	| GameMatchStatus.Cancelled;

/**
 * The slice of the status precedence that OO's fixture fields decide on their own.
 * Backend deriver and frontend badge both run this, so the ordering rule below exists
 * in exactly one place rather than once per enum vocabulary.
 *
 * Returns null when OO reports a plain not-started / in-progress fixture — then the
 * caller's own rules take over (the clock on the backend, the trading window on the
 * frontend).
 */
export const deriveFixtureMatchStatus = (
	fixtureStatus: string | null | undefined,
	retirementInfo: RetirementInfo | null | undefined,
	isCompleted?: boolean,
	hasScore?: boolean
): FixtureMatchStatus | null => {
	const completed = isCompleted ?? isOpticOddsCompleted(fixtureStatus);
	// OO marks walkover/retirement as `completed`, so retirement must win over Final.
	if (retirementInfo?.walkover) return GameMatchStatus.Walkover;
	if (retirementInfo) return GameMatchStatus.Retired;
	if (fixtureStatus === OpticOddsFixtureStatus.Cancelled) {
		// OO reports the same 'cancelled' status whether or not any play happened —
		// a recorded score means the match was abandoned mid-play; no score means it
		// never started at all.
		return hasScore ? GameMatchStatus.Abandoned : GameMatchStatus.Cancelled;
	}
	if (completed) return GameMatchStatus.Final;
	if (isOpticOddsSuspended(fixtureStatus)) return GameMatchStatus.Suspended;
	return null;
};

/** Raw-fixture counterpart of isTerminalGameMatchStatus, for callers holding a payload. */
export const isTerminalFixture = (
	fixtureStatus: string | null | undefined,
	retirementInfo: RetirementInfo | null | undefined
): boolean =>
	isTerminalGameMatchStatus(
		deriveFixtureMatchStatus(fixtureStatus, retirementInfo) ?? GameMatchStatus.Scheduled
	);

/** The OO clock fields that decide walkover / retirement / suspended. */
export type FixtureClockFields = {
	fixtureStatus?: string | null;
	retirementInfo?: RetirementInfo | null;
	isCompleted?: boolean;
	isLive?: boolean | null;
	hasScore?: boolean | null;
};

const fromFixtureFields = (fields: FixtureClockFields | null | undefined) =>
	deriveFixtureMatchStatus(
		fields?.fixtureStatus,
		fields?.retirementInfo,
		fields?.isCompleted,
		fields?.hasScore ?? undefined
	);

export const isWalkoverFixture = (fields: FixtureClockFields | null | undefined): boolean =>
	fromFixtureFields(fields) === GameMatchStatus.Walkover;

/**
 * OO does not mark a cancelled fixture `completed`, unlike walkover/retirement — so
 * callers that gate on `isCompleted` before voiding must check this first, or a
 * cancelled fixture never reaches the void branch. Also matches Abandoned: OO reports
 * the same 'cancelled' fixture status for both, distinguished only by score presence.
 */
export const isCancelledFixture = (fields: FixtureClockFields | null | undefined): boolean => {
	const fromFixture = fromFixtureFields(fields);
	return fromFixture === GameMatchStatus.Cancelled || fromFixture === GameMatchStatus.Abandoned;
};

export const isAbnormalFixtureEnd = (fields: FixtureClockFields | null | undefined): boolean => {
	const fromFixture = fromFixtureFields(fields);
	return (
		fromFixture === GameMatchStatus.Walkover ||
		fromFixture === GameMatchStatus.Retired ||
		isCancelledFixture(fields)
	);
};

export const isSuspendedFixture = (fields: FixtureClockFields | null | undefined): boolean =>
	fromFixtureFields(fields) === GameMatchStatus.Suspended;

/** Still on court: OO live flag or a rain-delay / suspension. */
export const isOnCourtFixture = (fields: FixtureClockFields | null | undefined): boolean =>
	fields?.isLive === true || isSuspendedFixture(fields);

/**
 * Profile Forecast History "Live Now" vs "Settled" split.
 *
 * A suspension is still an ongoing match, so it stays in Live Now even once the clock
 * window has passed. Walkover and retirement are the opposite case: the match is over,
 * yet a walkover's closing_time is still in the future (it was called off before the
 * scheduled start), so the clock alone would wrongly keep it in Live Now.
 */
export const isLobbyActivityOngoing = (
	closingTime: Date | string,
	result?: FixtureClockFields | null,
	nowMs: number = Date.now()
): boolean => {
	if (isSuspendedFixture(result)) return true;
	if (isAbnormalFixtureEnd(result)) return false;

	return new Date(closingTime).getTime() > nowMs;
};

/**
 * isLive is passed in rather than sniffed from the payload: callers already know it
 * (OO reports it as the top-level `status === 'live'`), and the shape differs between
 * the SSE stream and stored results.
 */
export const deriveGameMatchStatus = (gc: GameContext, isLive: boolean): GameMatchStatus => {
	const fromFixture = deriveFixtureMatchStatus(
		gc.fixtureStatus,
		gc.retirementInfo,
		gc.isCompleted,
		gc.hasScore
	);
	if (fromFixture) return fromFixture;

	if (isLive) {
		const firstHalfDone =
			gc.isScopeCompleted(PeriodType.Half1) || gc.isScopeCompleted(PeriodType.Period1);
		const secondHalfDone =
			gc.isScopeCompleted(PeriodType.Half2) || gc.isScopeCompleted(PeriodType.Period2);
		return firstHalfDone && !secondHalfDone ? GameMatchStatus.Halftime : GameMatchStatus.Live;
	}

	return GameMatchStatus.Scheduled;
};
